import { Container, Grid, Paper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DoneIcon from "@material-ui/icons/Done";
import EditIcon from "@material-ui/icons/Edit";
import { App, Events, Workouts } from "app-interfaces";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { useStateValue } from "../../config/AppContext";
import CatalogClass from "../../models/Catalog";
import { Action } from "../../types/StaticTypes";
import { EditSup } from "./Edit";
import SupersetSection from "./SupersetSection";

const useStyles = makeStyles((theme) => ({
  paper: {
    paddingBottom: 15,
    [theme.breakpoints.up("md")]: {
      paddingBottom: 30,
    },
    "& .section-container": {
      padding: 15,
      "& h5": {
        marginTop: 15,
        marginBottom: 15,
        fontSize: 16,
        fontWeight: 600,
        color: "#525252",
        textTransform: "none",
        overflowWrap: "break-word",
      },
      "& .MuiTypography-body1": {
        backgroundColor: "#F8F8F8",
        height: 83,
        overflow: "auto",
        marginBottom: 15,
        padding: 15,
        fontSize: 14,
        color: "#4A4A4A",
      },
      "& .stats-container": {
        border: "solid 6px rgba(90, 154, 229, 0.19)",
        borderRadius: "50%",
        height: 70,
        width: 70,
        padding: 3,
        margin: "auto",
        color: "#3e7fe9",
        [theme.breakpoints.up("md")]: {
          height: 90,
          width: 90,
        },
        "& .stats-background": {
          backgroundColor: "#3e7fe9",
          borderRadius: "50%",
          height: 52,
          width: 52,
          padding: "10px 0",
          [theme.breakpoints.up("md")]: {
            height: 72,
            width: 72,
            padding: "15px 0px",
          },
          "& .MuiTypography-subtitle1": {
            color: "#ffffff",
            marginTop: -6,
            [theme.breakpoints.up("md")]: {
              marginTop: -8,
            },
          },
          "& .MuiTypography-body2": {
            color: "#ffffff",
          },
        },
        "& .MuiTypography-subtitle1": {
          marginTop: 2,
          fontSize: 16,
          fontWeight: "bold",
          textAlign: "center",
          textTransform: "uppercase",
          [theme.breakpoints.up("md")]: {
            marginTop: 5,
          },
        },
        "& .MuiTypography-body2": {
          fontSize: 10,
          textAlign: "center",
          textTransform: "capitalize",
          [theme.breakpoints.up("md")]: {
            fontSize: 14,
          },
        },
      },
    },
  },
  spaceBtw: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  editText: {
    color: "var(--text-primary)",
    cursor: "pointer",
  },
  inputStyle: {
    width: "100%",
    height: 83,
    fontSize: 14,
    padding: 15,
    backgroundColor: "#f8f8f8",
    border: "none",
    marginBottom: 15,
  },
  saveBtn: {
    border: "none",
    color: "var(--text-primary)",
    backgroundColor: "inherit",
  },
}));

export type SectionProps = {
  workout: Workouts.IWorkout;
  session?: Events.ISession;
  dispatch?: React.Dispatch<Action>;
};

const WorkoutsDetailComponent = (props: any) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const workoutReducer = (workout: Workouts.IWorkout, action: Action) => {
    const handleSubmit = (updatedWorkout: Workouts.IWorkout) =>
      Methods.updateWorkouts(updatedWorkout);

    switch (action.type) {
      case "add":
        workout = action.payload;
        handleSubmit(workout);
        break;
      case "update":
        workout.exercise_super_set[action.payload.targetIndex].exercises = JSON.parse(
          action.payload.newSuperSet
        );
        handleSubmit(workout);
        break;
      case "update-exercise":
        workout.exercise_super_set[action.payload.supersetIndex].exercises[
          action.payload.exerciseIndex
        ] = action.payload.exercise;
        handleSubmit(workout);
        break;
      default:
        throw new Error(t(`${action.type} is not a valid option`));
    }
    return workout;
  };

  const { catalog, Methods } = useStateValue() as {
    catalog: CatalogClass;
    Methods: App.Methods;
  };
  const editSup = useState(false);
  const { id } = props.match.params;
  const templateWorkout = {
    id: "",
    name: "",
    description: "",
    duration: 0,
    is_deleted: false,
    exercise_super_set: [] as Workouts.IExerciseSuperSet[],
  };

  const catalogWorkout = catalog.Workouts.find((el: Workouts.IWorkout) => el.id === id);
  const initialWorkout = catalogWorkout ? catalogWorkout : templateWorkout;
  const [stateWorkout, setStateWorkout] = React.useReducer(workoutReducer, initialWorkout);
  return !id || !catalogWorkout ? (
    <Paper id="workouts-details">{t("No Workout Found")}</Paper>
  ) : (
    <Paper id="workout-details" elevation={0} className={classes.paper}>
      <TitleSection workout={stateWorkout} />
      <InfoSection workout={stateWorkout} />
      {editSup[0] ? (
        <EditSup id={id} editSup={editSup} dispatch={setStateWorkout} />
      ) : (
        <SupersetSection workout={stateWorkout} dispatch={setStateWorkout} editSup={editSup} />
      )}
    </Paper>
  );
};

const TitleSection: React.FC<SectionProps> = ({ workout }) => {
  const { t } = useTranslation();
  return (
    <Container className="section-container">
      <Typography variant="h5">{workout.name}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={4}>
          <div className="stats-container">
            <Typography variant="subtitle1">{workout.exercise_super_set.length}</Typography>
            <Typography variant="body2">{t("Exercises")}</Typography>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div className="stats-container">
            <div className="stats-background">
              <Typography variant="subtitle1" style={{ fontSize: "15px" }}>
                {workout.duration}
              </Typography>
              <Typography variant="body2">{t("Mins")}</Typography>
            </div>
          </div>
        </Grid>
      </Grid>
    </Container>
  );
};

const InfoSection: React.FC<SectionProps> = ({ workout }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { catalog, Methods } = useStateValue() as {
    catalog: CatalogClass;
    Methods: App.Methods;
  };
  const catalogWorkout = catalog.Workouts.find((el) => el.id === workout.id);
  const [editDesc, setEditDesc] = useState(false);
  const [editNotes, setEditNotes] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.currentTarget);
    let params = { ...catalogWorkout };
    formData.forEach((value, name) => {
      if (name === "exercise_super_set") return;
      return Object.assign(params, { [name]: value });
    });
    return Methods.updateWorkouts(params).then(() => {
      setEditDesc(false);
      setEditNotes(false);
      setLoading(false);
    });
  };

  return (
    <Container className="section-container">
      <form onSubmit={handleSave}>
        <div className={classes.spaceBtw}>
          <Typography variant="h5">{t("Workout description")} </Typography>
          {!editDesc ? (
            <div className={classes.editText} onClick={(_) => setEditDesc(true)}>
              <EditIcon fontSize="small" />
              {t("Edit")}
            </div>
          ) : (
            <button type="submit" className={classes.saveBtn} disabled={loading}>
              <DoneIcon fontSize="small" /> {t("Done")}{" "}
            </button>
          )}
        </div>
        {!editDesc ? (
          <Typography variant="body1">
            {catalogWorkout ? catalogWorkout.description : ""}
          </Typography>
        ) : (
          <textarea
            id="description"
            name="description"
            className={classes.inputStyle}
            placeholder={t("DESCRIPTION")}
            defaultValue={catalogWorkout ? catalogWorkout.description : ""}
            ref={(input) => input && input.focus()}
            required
            title=""
          />
        )}
        <div className={classes.spaceBtw}>
          <Typography variant="h5">{t("Workout notes")}</Typography>
          {!editNotes ? (
            <div className={classes.editText} onClick={(_) => setEditNotes(true)}>
              <EditIcon fontSize="small" />
              {t("Edit")}
            </div>
          ) : (
            <button type="submit" className={classes.saveBtn} disabled={loading}>
              <DoneIcon fontSize="small" /> {t("Done")}{" "}
            </button>
          )}
        </div>

        {!editNotes ? (
          <Typography variant="body1">{catalogWorkout ? catalogWorkout.notes : ""}</Typography>
        ) : (
          <textarea
            id="notes"
            name="notes"
            className={classes.inputStyle}
            placeholder={t("Notes")}
            defaultValue={catalogWorkout ? catalogWorkout.notes : ""}
            ref={(input) => input && input.focus()}
          />
        )}
      </form>
    </Container>
  );
};

export default WorkoutsDetailComponent;
